/* ══════════════════════════════════════════════════════════════════════════
   HAVANI — GLOBAL THEME BEHAVIOUR

   Everything here is a custom element. That is deliberate: Shopify's theme
   editor re-renders a single section's HTML in place, and `connectedCallback`
   fires again for anything inside it while `disconnectedCallback` tears the
   old one down. No `DOMContentLoaded`, no `shopify:section:load` bookkeeping,
   and no chance of binding the same listener twice.

   Business logic is not duplicated per design system. There is one cart, one
   search, one drawer. Blue Gradient and Beach Gradient differ in CSS only.
   ══════════════════════════════════════════════════════════════════════════ */

window.HV = window.HV || {};

(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  HV.$ = $;
  HV.$$ = $$;

  HV.routes = window.routes || {};
  HV.strings = window.hvStrings || {};

  /* ── overlay manager ───────────────────────────────────────────────────
     One place decides what is open, so the scrim, the body scroll lock and
     focus never disagree with each other.                                */
  HV.overlays = {
    current: null,
    lastFocus: null,
    open(el) {
      if (!el) return;
      this.close();
      this.lastFocus = document.activeElement;
      this.current = el;
      el.classList.add('is-open');
      el.removeAttribute('hidden');
      const scrim = $('#hv-scrim');
      if (scrim) scrim.classList.add('is-open');
      document.body.classList.add('overlay-open');
      const focusable = el.querySelector('[autofocus], input, button, a[href]');
      if (focusable) setTimeout(() => focusable.focus(), 60);
      document.addEventListener('keydown', this._trap);
    },
    close() {
      document.removeEventListener('keydown', this._trap);
      $$('.is-open').forEach((el) => {
        if (el.matches('.drawer, .search-overlay, .cart-drawer, .facets-sheet, .lightbox, .viewer')) {
          el.classList.remove('is-open');
        }
      });
      const scrim = $('#hv-scrim');
      if (scrim) scrim.classList.remove('is-open');
      document.body.classList.remove('overlay-open');
      $$('[aria-expanded="true"][data-overlay-toggle]').forEach((b) =>
        b.setAttribute('aria-expanded', 'false')
      );
      if (this.lastFocus && this.current) {
        try { this.lastFocus.focus(); } catch (e) { /* element may be gone */ }
      }
      this.current = null;
    },
    /* keep Tab inside an open dialog */
    _trap(e) {
      if (e.key === 'Escape') { HV.overlays.close(); return; }
      if (e.key !== 'Tab' || !HV.overlays.current) return;
      const items = $$(
        'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])',
        HV.overlays.current
      ).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  /* ── toast ─────────────────────────────────────────────────────────────
     Also an aria-live region, so the same feedback reaches a screen reader. */
  HV.toast = (message, isError) => {
    let el = $('#hv-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'hv-toast';
      el.className = 'toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.toggle('toast--error', !!isError);
    el.classList.add('is-on');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-on'), 2600);
  };

  /* ── section rendering ─────────────────────────────────────────────────
     Shopify's Section Rendering API is how the cart drawer, the cart page,
     the header count and the facet grid all stay in sync off one request. */
  HV.renderSections = async (sections, url) => {
    const base = url || window.location.pathname;
    const sep = base.indexOf('?') > -1 ? '&' : '?';
    const res = await fetch(`${base}${sep}sections=${sections.join(',')}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!res.ok) throw new Error('Section render failed');
    return res.json();
  };

  HV.replaceSection = (id, html, selector) => {
    const target = selector ? $(selector) : $(`#shopify-section-${id}`);
    if (!target || !html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const source = selector
      ? parsed.querySelector(selector)
      : parsed.getElementById(`shopify-section-${id}`);
    if (source) target.innerHTML = source.innerHTML;
  };

  /* ── cart ──────────────────────────────────────────────────────────────
     Thin wrapper over Shopify's cart AJAX API. No cart state is kept in
     JavaScript — Shopify is the source of truth and every mutation returns
     freshly rendered sections.                                            */
  HV.cart = {
    sections: ['cart-drawer', 'header'],
    async add(formDataOrId, quantity) {
      let body;
      if (formDataOrId instanceof FormData) {
        body = formDataOrId;
        body.append('sections', this.sections.join(','));
      } else {
        body = JSON.stringify({
          items: [{ id: formDataOrId, quantity: quantity || 1 }],
          sections: this.sections
        });
      }
      const opts = { method: 'POST', body };
      if (typeof body === 'string') opts.headers = { 'Content-Type': 'application/json' };
      const res = await fetch(`${HV.routes.cart_add_url || '/cart/add'}.js`, opts);
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to cart');
      this.paint(data.sections);
      document.dispatchEvent(new CustomEvent('hv:cart:updated', { detail: data }));
      return data;
    },
    async change(payload) {
      const res = await fetch(`${HV.routes.cart_change_url || '/cart/change'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ sections: this.sections.concat(['main-cart']) }, payload))
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not update cart');
      this.paint(data.sections);
      document.dispatchEvent(new CustomEvent('hv:cart:updated', { detail: data }));
      return data;
    },
    async update(payload) {
      const res = await fetch(`${HV.routes.cart_update_url || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({ sections: this.sections.concat(['main-cart']) }, payload))
      });
      const data = await res.json();
      this.paint(data.sections);
      document.dispatchEvent(new CustomEvent('hv:cart:updated', { detail: data }));
      return data;
    },
    paint(sections) {
      if (!sections) return;
      if (sections['cart-drawer']) {
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const fresh = parsed.querySelector('[data-cart-drawer-contents]');
        const live = $('[data-cart-drawer-contents]');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      }
      if (sections['header']) {
        const parsed = new DOMParser().parseFromString(sections['header'], 'text/html');
        const fresh = parsed.querySelector('[data-cart-count]');
        $$('[data-cart-count]').forEach((node) => {
          if (fresh) node.innerHTML = fresh.innerHTML;
          node.classList.add('is-bumped');
          setTimeout(() => node.classList.remove('is-bumped'), 450);
        });
      }
      if (sections['main-cart']) {
        const parsed = new DOMParser().parseFromString(sections['main-cart'], 'text/html');
        const fresh = parsed.querySelector('[data-main-cart]');
        const live = $('[data-main-cart]');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      }
    }
  };

  /* ══ custom elements ════════════════════════════════════════════════════ */

  /* header: hides on scroll down, exposes the mega menu, owns the burger */
  class HvHeader extends HTMLElement {
    connectedCallback() {
      this.last = 0;
      this.chrome = this.closest('.chrome') || this;
      this.onScroll = this.onScroll.bind(this);

      /* One AbortController per connection, same convention as every other
         component here. These listeners used to be added with no way to
         remove them, so a section re-render left a second copy of every
         one — including a second "close the menu" on scroll. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      addEventListener('scroll', this.onScroll, { passive: true, signal });
      this.measure();
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.chrome);

      /* WHICH INPUT MOVED FOCUS LAST.

         A mouse press focuses the trigger during mousedown, and focusin opened
         the dropdown there — a frame before the click handler ran. Two things
         followed: the panel flashed open on its way out to the link, and the
         click handler's "is it already open?" guard could never see a closed
         item, so its open-instead-of-navigate branch was dead code on desktop.

         :focus-visible is the browser's own answer to "is this focus the user
         needs to see?" — true for Tab, false for a mouse press on a link — so
         it is the test. The flag is only a fallback for engines without it. */
      addEventListener('pointerdown', () => { this.viaPointer = true; }, { capture: true, passive: true, signal });
      addEventListener('keydown', (e) => { if (e.key === 'Tab') this.viaPointer = false; }, { capture: true, signal });

      this.megaItems = $$('.nav-item[data-mega]', this);
      this.megaItems.forEach((item) => this.bindMega(item, signal));

      document.addEventListener('click', (e) => {
        if (!e.target.closest('.nav-item[data-mega]')) this.closeMega();
      }, { signal });

      /* SCROLLING A LITTLE MUST NOT CLOSE THE MENU. This was an unconditional
         closeMega() on every scroll event, so a trackpad nudge, a moment of
         inertial scrolling still settling, or the tiny scroll a click can
         cause all shut the dropdown mid-read — the single biggest reason it
         felt impossible to navigate. Only a deliberate scroll away from where
         the menu was opened closes it now. */
      this.openedAt = 0;
      addEventListener('scroll', () => {
        if (!this.megaOpen) return;
        if (Math.abs(window.scrollY - this.openedAt) > 56) this.closeMega();
      }, { passive: true, signal });

      addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeMega(); }, { signal });
    }
    disconnectedCallback() {
      if (this.controller) { this.controller.abort(); this.controller = null; }
      if (this.ro) { this.ro.disconnect(); this.ro = null; }
      /* AbortController does not cancel timers. The theme editor moves a
         section's node on reorder, so a pending open could otherwise fire
         against a detached tree and leave megaOpen true for the next
         connection — the menu then thinks it is open while nothing is. */
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.tabTimer);
      this.megaOpen = false;
    }
    measure() {
      document.documentElement.style.setProperty('--chrome-h', this.chrome.offsetHeight + 'px');
    }
    isKeyboardFocus(el) {
      try { return el.matches(':focus-visible'); } catch (_) { return !this.viaPointer; }
    }
    onScroll() {
      const y = window.scrollY;
      this.chrome.classList.toggle('is-up', y > this.last && y > 340 && !document.body.classList.contains('overlay-open'));
      this.last = y;
    }
    bindMega(item, signal) {
      const panel = $('.mega', item);
      const trigger = $('a, button', item);
      if (!panel || !trigger) return;
      const desktop = () => !matchMedia('(max-width:900px)').matches;

      /* HOVER INTENT, BOTH WAYS.

         OPEN_WAIT stops the menu firing at anything the cursor merely crosses.
         Sweeping the pointer from the logo to the bag used to throw open every
         dropdown it passed over, which is most of what "very sensitive" meant.

         CLOSE_WAIT was 200ms, which is under the time it takes to travel from
         a nav item to something at the far side of a full-width panel, or to
         clip a corner on the way. 420ms is forgiving without feeling stuck. */
      const OPEN_WAIT = 110;
      const CLOSE_WAIT = 420;
      /* Hover intent for the category tabs, for the same reason OPEN_WAIT
         exists on the trigger: a pointer merely crossing the rail on its way
         to a card must not swap the panel out from under it. Slightly longer
         than OPEN_WAIT because the rail is directly in the travel path. */
      const TAB_WAIT = 160;
      /* THE TIMERS BELONG TO THE HEADER, NOT TO THE ITEM.

         They used to be two closures per nav item — but closeMega() closes
         EVERY item, so the ownership did not match what the timer does. Moving
         from one dropdown to the next left the FIRST item's close timer armed
         and unreachable: entering the second item called cancelAll(), which
         only ever saw its own closures. The second dropdown opened instantly,
         then ~420ms later the orphan fired closeMega() and shut the whole menu
         while the cursor was sitting still inside it. Nothing re-arms after
         that — pointerenter had already fired — so it stayed dead until the
         pointer left the nav and came back. That was "switching categories".

         One shared menu, therefore one pending close. Entering ANY item
         cancels it, whichever item armed it. */
      /* TWO cancels, and the distinction is load-bearing. pointerenter does not
         bubble, but the browser dispatches it to EVERY newly entered element in
         the chain — so entering the trigger fires it on .nav-item and then on
         the <a> inside it. A single cancel() on the trigger therefore ran after
         the item had armed the open timer and cleared it, and the menu could
         never open at all. Only the close timer may be cancelled from inside. */
      const cancelClose = () => { clearTimeout(this.closeTimer); };
      const cancelAll = () => { clearTimeout(this.openTimer); clearTimeout(this.closeTimer); };

      const open = () => {
        cancelAll();
        /* close the OTHERS, not this one — closeMega() used to run first, which
           stripped has-mega-open off the header and re-added it in the same
           frame, flickering the header's border and corners on every open */
        this.megaItems?.forEach((other) => {
          if (other === item) return;
          other.classList.remove('is-open');
          $('.mega', other)?.classList.remove('is-open');
          $('a, button', other)?.setAttribute('aria-expanded', 'false');
        });
        item.classList.add('is-open');
        panel.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        $('.site-header', this.closest('.chrome') || document)?.classList.add('has-mega-open');
        this.megaOpen = true;
        this.openedAt = window.scrollY;
      };
      const scheduleClose = () => {
        cancelAll();
        this.closeTimer = setTimeout(() => this.closeMega(), CLOSE_WAIT);
      };

      item.addEventListener('pointerenter', (e) => {
        if (!desktop()) return;
        /* TOUCH AND PEN DO NOT HOVER, SO THEY MUST NOT OPEN FROM HERE.
           A tap dispatches pointerenter before click. This branch used to treat
           every non-mouse pointer as "open immediately", so by the time the
           click handler below asked `is-open?` the answer was already yes — its
           guard fell through, the link was followed, and the panel that had
           just opened was torn down by the navigation. The net effect on a
           touch screen wider than 900px was that the dropdown could never be
           read: the first tap always went straight to the category page.
           Opening on tap is the click handler's job; this one is hover intent
           and hover intent only. */
        if (e.pointerType !== 'mouse') return;
        cancelAll();
        /* a pointer already on another open dropdown switches immediately —
           waiting there would feel broken, not careful */
        if (this.megaOpen) { open(); return; }
        this.openTimer = setTimeout(open, OPEN_WAIT);
      }, { signal });
      item.addEventListener('pointerleave', scheduleClose, { signal });
      /* the panel is a descendant of the item, so this is belt and braces for
         the case where a child element swallows the boundary event */
      panel.addEventListener('pointerenter', cancelClose, { signal });
      panel.addEventListener('pointerleave', scheduleClose, { signal });
      /* moving back onto the trigger after leaving must cancel the close —
         and must NOT touch the open timer the item has just armed */
      trigger.addEventListener('pointerenter', cancelClose, { signal });

      /* A MOUSE CLICK NAVIGATES — the panel is already reachable by hover, and
         swallowing the click to open something the pointer has almost always
         opened already just made the nav feel unresponsive.

         Touch and pen have no hover, so there the first tap must still open the
         panel rather than follow the link, or the dropdown is unreachable on a
         touch screen wider than 900px. desktop() is a WIDTH test, not a pointer
         test, which is why that case has to be handled here rather than assumed
         away. A keyboard Enter arrives with no pointerType and navigates, which
         is right: the user activated the link deliberately. */
      trigger.addEventListener('click', (e) => {
        if (!desktop()) return;
        if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        if (!item.classList.contains('is-open')) { e.preventDefault(); open(); }
      }, { signal });
      /* Tab opens the panel so the keyboard can reach it; a mouse press must
         not, or it flashes open on the way to the link it just followed */
      item.addEventListener('focusin', (e) => {
        if (desktop() && this.isKeyboardFocus(e.target)) open();
      }, { signal });

      /* SHOW a category's panel. Pure preview: it never navigates and never
         touches the event, which is the whole reason it exists as a function.

         ⚠️ THIS USED TO BE DONE BY CALLING `tab.click()`. That was safe while a
         tab was a <button type="button">. A tab is now an <a href> (a category
         is a destination — see sections/header.liquid), and `.click()` on an
         anchor FOLLOWS THE LINK. Left as it was, simply resting the pointer on
         "Necklaces" would have navigated to it after TAB_WAIT. Never reintroduce
         a synthetic click here. */
      const selectTab = (tab) => {
        cancelClose();
        $$('.mega__tab', panel).forEach((t) => t.setAttribute('data-current', 'false'));
        tab.setAttribute('data-current', 'true');
        $$('[data-mega-panel]', panel).forEach((p) => {
          p.hidden = p.dataset.megaPanel !== tab.dataset.megaTab;
        });
      };

      $$('.mega__tab', panel).forEach((tab) => {
        /* No click handler at all, and that is the fix. The old one called
           preventDefault(), so the category could never be reached. Activation
           now does what the href says on every input type — mouse, touch and
           the keyboard's Enter alike. */
        /* HOVERING A CATEGORY SHOWS IT — BUT ONLY IF THE POINTER MEANT IT.
           This used to be a bare `tab.click()` on pointerenter, and it made the
           whole dropdown unreliable to click. The tab rail sits between the nav
           trigger and the featured cards (measured at 1440: rail x 48-244, cards
           from x 311, trigger at x 87), so EVERY natural diagonal from the
           trigger to a card crosses it. Each tab the pointer passed through
           swapped the panel underneath the cursor, and because three of the five
           categories have no products yet, the card being aimed at was commonly
           replaced by .mega__empty before the click landed — a div with no href,
           so the click did nothing at all and the menu just sat there.

           The trigger already solves this with OPEN_WAIT; the tabs simply never
           got the same treatment. A pointer travelling to a card crosses a tab
           in well under TAB_WAIT and never activates it; a pointer that rests on
           a category still switches, which is the feature. A real click is
           unaffected and stays instant, as does the keyboard.

           MOUSE ONLY, and now doubly so: on touch there is no hover, and this
           firing at all would preview a category the finger was only passing
           over on its way to the link it meant to tap. */
        tab.addEventListener('pointerenter', (e) => {
          if (!desktop() || e.pointerType !== 'mouse') return;
          clearTimeout(this.tabTimer);
          this.tabTimer = setTimeout(() => selectTab(tab), TAB_WAIT);
        }, { signal });
        tab.addEventListener('pointerleave', () => { clearTimeout(this.tabTimer); }, { signal });
      });
    }
    closeMega() {
      /* a pending OPEN must die here too. Escape, an outside click or a scroll
         landing inside the 110ms open intent otherwise left the timer running,
         and the menu opened by itself a moment after being dismissed. */
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      /* and a pending TAB switch — one armed just before Escape would otherwise
         fire against a menu that is already closed */
      clearTimeout(this.tabTimer);
      this.megaOpen = false;
      this.megaItems?.forEach((item) => {
        item.classList.remove('is-open');
        $('.mega', item)?.classList.remove('is-open');
        $('a, button', item)?.setAttribute('aria-expanded', 'false');
      });
      $('.site-header')?.classList.remove('has-mega-open');
    }
  }
  if (!customElements.get('hv-header')) customElements.define('hv-header', HvHeader);

  /* a button that opens one of the overlays */
  class HvToggle extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-overlay-toggle]');
        if (!btn) return;
        e.preventDefault();
        const target = $(btn.getAttribute('data-overlay-toggle'));
        if (!target) return;
        const isOpen = target.classList.contains('is-open');
        HV.overlays.close();
        if (!isOpen) {
          HV.overlays.open(target);
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    }
  }
  if (!customElements.get('hv-toggle')) customElements.define('hv-toggle', HvToggle);

  /* announcement bar: rotates, dismisses, remembers the dismissal */
  class HvAnnouncement extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement__slides > *', this);
      this.index = 0;
      if (this.slides.length > 1 && !matchMedia('(prefers-reduced-motion:reduce)').matches) {
        this.timer = setInterval(() => this.next(), Number(this.dataset.interval || 4200));
      }
      const close = $('.announcement__close', this);
      if (close) {
        close.addEventListener('click', () => {
          this.remove();
          try { sessionStorage.setItem('hv-annc-dismissed', '1'); } catch (e) { /* private mode */ }
          $('hv-header')?.measure?.();
        });
      }
      try {
        if (sessionStorage.getItem('hv-annc-dismissed') === '1' && !window.Shopify?.designMode) this.remove();
      } catch (e) { /* ignore */ }
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.slides[this.index].classList.remove('is-on');
      this.index = (this.index + 1) % this.slides.length;
      this.slides[this.index].classList.add('is-on');
    }
  }
  if (!customElements.get('hv-announcement')) customElements.define('hv-announcement', HvAnnouncement);

  /* predictive search — Shopify's own endpoint, rendered by a theme section */
  class HvSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"], input[name="q"]', this);
      this.results = $('[data-search-results]', this);
      this.abort = null;
      if (!this.input) return;
      this.input.addEventListener('input', () => this.schedule());
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') HV.overlays.close();
      });
    }
    schedule() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.fetch(), 220);
    }
    async fetch() {
      const q = this.input.value.trim();
      if (!this.results) return;
      if (q.length < 2) {
        this.results.innerHTML = this.dataset.defaultHtml || '';
        return;
      }
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      const url =
        `${HV.routes.predictive_search_url || '/search/suggest'}` +
        `?q=${encodeURIComponent(q)}` +
        `&resources[type]=product,page,article` +
        `&resources[limit]=6` +
        `&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: this.abort.signal });
        const text = await res.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = parsed.querySelector('[data-predictive-results]');
        this.results.innerHTML = fresh ? fresh.innerHTML : '';
      } catch (e) {
        if (e.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  if (!customElements.get('hv-search')) customElements.define('hv-search', HvSearch);

  /* horizontal drag rail with a hairline position indicator */
  class HvRail extends HTMLElement {
    connectedCallback() {
      this.rail = $('.rail', this);
      this.bar = $('.rail-bar i', this);
      if (!this.rail) return;

      /* One AbortController per connection. Reordering a section in the theme
         editor moves the existing DOM node rather than rebuilding it, so this
         same instance is disconnected and reconnected — every listener below
         would otherwise be bound a second time. */
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      this.sync = this.sync.bind(this);
      this.sync();
      addEventListener('resize', this.sync, { signal });
      this.rail.addEventListener('scroll', this.sync, { passive: true, signal });

      /* Card width comes from a flex-basis rather than from the image, so the
         rail measures correctly before media loads. It still measures zero
         while the section is off-layout, which is what this re-measures. */
      this.ro = new ResizeObserver(this.sync);
      this.ro.observe(this.rail);

      /* ---- press, drag, release ------------------------------------------
         Three decisions make this feel like the page moving under the hand
         rather than a widget being wrestled.

         It starts anywhere on the rail, cards included. The previous build
         bailed out of the drag whenever the press landed on `a, button` —
         which is nearly the entire surface of a product card — so a press on
         a card fell through to the browser instead: titles highlighted,
         photographs tore out as ghost images, and the rail only moved if you
         found one of the gaps between cards. That fall-through was the whole
         "sticky" feeling.

         Nothing happens until the pointer has travelled far enough to mean
         it. Under the threshold the press is still a press, so a card still
         opens, quick-add still fires, and the viewer still opens.

         Touch is left completely alone. A rail is already a native inertial
         scroller on a touch screen; driving scrollLeft from script on top of
         that scrolls the same box twice and fights the thumb. Native touch
         scrolling is the best-feeling thing available here, so it is what
         touch gets — this is mouse only. */
      const DRAG_MIN = 6;
      let armed = false, dragging = false, swallow = false, pid = null;
      let startX = 0, startLeft = 0;
      let trail = [];

      /* Measured at press time rather than read off the overflow attribute
         sync() publishes: that attribute is deliberately absent when the rail
         could not be measured (fail-open, so the affordance still shows), and
         absent must never be read here as "draggable". */
      const overflows = () => this.rail.scrollWidth - this.rail.clientWidth > 1;

      const release = () => {
        if (!armed && !dragging) return;
        /* Only a gesture that actually became a drag swallows the click that
           follows it. Set before the click fires — pointerup, then
           lostpointercapture, then click. */
        if (dragging) {
          swallow = true;
          /* THROW + SETTLE. Releasing used to stop the rail dead and then let
             CSS snap yank it to a card edge. Now the rail carries on with the
             speed of the hand for a moment and glides to the nearest card. */
          const now = performance.now();
          const recent = trail.filter((p) => now - p.t < 90);
          let v = 0;
          if (recent.length > 1) {
            const a = recent[0], b = recent[recent.length - 1];
            v = (b.x - a.x) / Math.max(16, b.t - a.t);
          }
          this.glideTo(this.snapFor(this.rail.scrollLeft - v * 320, v));
        }
        trail = [];
        if (pid !== null && this.rail.hasPointerCapture(pid)) this.rail.releasePointerCapture(pid);
        armed = false; dragging = false; pid = null;
        this.rail.classList.remove('is-dragging');
      };

      this.rail.addEventListener('pointerdown', (e) => {
        /* A drag that ended without a click left the swallow armed. Clear it
           on the next press so it can never eat a genuine click later. */
        swallow = false;
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        if (!overflows()) return;
        /* A caret and a text selection are the point of these, so they keep
           the press. Nothing else in a rail needs it. */
        if (e.target.closest('input, textarea, select, [contenteditable]')) return;
        armed = true;
        pid = e.pointerId;
        startX = e.clientX;
        startLeft = this.rail.scrollLeft;
      }, { signal });

      this.rail.addEventListener('pointermove', (e) => {
        if (!armed || e.pointerId !== pid) return;
        const dx = e.clientX - startX;
        if (!dragging) {
          if (Math.abs(dx) < DRAG_MIN) return;
          dragging = true;
          /* Capture only once it is a drag, so a plain click keeps entirely
             native semantics. It also keeps the gesture alive when the cursor
             leaves the card, or the rail, mid-drag. */
          this.rail.setPointerCapture(pid);
          this.rail.classList.add('is-dragging');
          /* The browser may have begun a selection inside the threshold.
             Drop it once, here — cheaper and less invasive than making the
             rail permanently unselectable. */
          const sel = getSelection();
          if (sel && !sel.isCollapsed) sel.removeAllRanges();
        }
        this.rail.scrollLeft = startLeft - dx;
        trail.push({ x: e.clientX, t: performance.now() });
        if (trail.length > 8) trail.shift();
        /* Kills the compatibility mouse events, and with them any further
           selection growth for the rest of the drag. */
        e.preventDefault();
      }, { signal });

      this.rail.addEventListener('pointerup', release, { signal });
      this.rail.addEventListener('pointercancel', release, { signal });
      this.rail.addEventListener('lostpointercapture', release, { signal });

      /* Capture phase, and it stops propagation outright: the click has to die
         before it reaches the card's own link and before it reaches the
         document-level listener that opens the media viewer. Both would
         otherwise fire at the end of a drag. */
      this.rail.addEventListener('click', (e) => {
        if (!swallow) return;
        swallow = false;
        e.preventDefault();
        e.stopPropagation();
      }, { capture: true, signal });

      /* Native HTML5 drag has no meaning in a carousel, and once it starts the
         ghost preview sticks to the cursor for the rest of the gesture. */
      this.rail.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

      this.bindBar(signal);
      this.bindArrows(signal);
    }

    /* card start positions, measured live (cards can differ in width) */
    stops() {
      const r = this.rail;
      const max = r.scrollWidth - r.clientWidth;
      const kids = Array.from(r.children);
      if (!kids.length) return [0];
      const base = kids[0].offsetLeft;
      const out = kids.map((k) => Math.min(max, Math.max(0, k.offsetLeft - base)));
      out.push(max);
      return Array.from(new Set(out.map((n) => Math.round(n)))).sort((a, b) => a - b);
    }
    snapFor(target, v) {
      const stops = this.stops();
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      target = Math.min(max, Math.max(0, target));
      let best = stops[0];
      stops.forEach((p) => { if (Math.abs(p - target) < Math.abs(best - target)) best = p; });
      return best;
    }
    glideTo(left) {
      const r = this.rail;
      r.classList.add('is-gliding');
      clearTimeout(this.glideTimer);
      r.scrollTo({ left, behavior: 'smooth' });
      const done = () => { r.classList.remove('is-gliding'); };
      if ('onscrollend' in window) r.addEventListener('scrollend', done, { once: true });
      this.glideTimer = setTimeout(done, 900);
    }

    /* PREV / NEXT. With a mouse and no trackpad the only ways along a rail
       were a drag or the thin bar — nothing said "there is more this way".
       Two round buttons ride the picture's midline; each moves one screen of
       cards and lands on a card edge. Hidden on touch, where a swipe is the
       native gesture, and disabled at either end. */
    bindArrows(signal) {
      if (this.querySelector(':scope > .rail-arrow')) return;
      const mk = (dir) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'rail-arrow rail-arrow--' + dir;
        b.setAttribute('aria-label', dir === 'prev' ? 'Previous' : 'Next');
        b.setAttribute('aria-controls', this.rail.id || '');
        b.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="' +
          (dir === 'prev' ? 'M14.5 6 8.5 12l6 6' : 'M9.5 6l6 6-6 6') +
          '" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        b.addEventListener('click', () => this.page(dir === 'next' ? 1 : -1), { signal });
        this.appendChild(b);
        return b;
      };
      this.prevBtn = mk('prev');
      this.nextBtn = mk('next');
      this.sync();
    }
    page(dir) {
      const r = this.rail;
      const view = r.clientWidth;
      const cur = r.scrollLeft;
      const stops = this.stops();
      let target;
      if (dir > 0) {
        /* the first card that is not fully visible becomes the first card */
        const kids = Array.from(r.children);
        const base = kids.length ? kids[0].offsetLeft : 0;
        const cut = kids.find((k) => k.offsetLeft - base + k.offsetWidth > cur + view + 2);
        target = cut ? cut.offsetLeft - base : r.scrollWidth;
        if (target <= cur + 4) target = cur + view;
      } else {
        target = cur - view;
        const back = stops.filter((p) => p >= target - 2);
        target = back.length ? back[0] : 0;
        if (target >= cur - 4) target = Math.max(0, cur - view);
      }
      this.glideTo(this.snapFor(target, 0));
    }

    /* ---- THE POSITION BAR IS A REAL SCROLLBAR -----------------------------
       It used to be drawn only: a 1px line with a 3px thumb and no events, so
       pressing or dragging it did nothing — the bar looked like a scrollbar
       and was not one. Now:
         · press anywhere on the track and the rail jumps there;
         · grab the thumb (mouse, pen or finger) and drag to scroll;
         · focus it and use ← → Home End;
       The hit area is 26px tall (see havani-fixes.css) so a 1px line is not
       the thing you have to aim at. Snap is suspended while the bar is held,
       for the same reason it is during a card drag. */
    bindBar(signal) {
      const track = $('.rail-bar', this);
      if (!track || !this.bar) return;
      this.track = track;
      if (!this.rail.id) this.rail.id = 'hv-rail-' + Math.random().toString(36).slice(2, 8);
      track.setAttribute('role', 'scrollbar');
      track.setAttribute('aria-controls', this.rail.id);
      track.setAttribute('aria-orientation', 'horizontal');
      track.setAttribute('aria-valuemin', '0');
      track.setAttribute('aria-valuemax', '100');
      track.setAttribute('aria-label', 'Scroll');
      track.tabIndex = 0;

      let held = false, grab = 0, pid = null;
      const geom = () => {
        const t = track.getBoundingClientRect();
        const b = this.bar.getBoundingClientRect();
        return { left: t.left, width: t.width, thumb: b.width, thumbLeft: b.left };
      };
      const scrollFromX = (clientX) => {
        const g = geom();
        const room = g.width - g.thumb;
        const max = this.rail.scrollWidth - this.rail.clientWidth;
        if (room <= 0 || max <= 0) return;
        const ratio = Math.min(1, Math.max(0, (clientX - g.left - grab) / room));
        this.rail.scrollLeft = ratio * max;
      };
      const end = () => {
        if (!held) return;
        held = false;
        if (pid !== null && track.hasPointerCapture(pid)) track.releasePointerCapture(pid);
        pid = null;
        track.classList.remove('is-held');
        this.rail.classList.remove('is-dragging');
      };

      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const g = geom();
        const onThumb = e.clientX >= g.thumbLeft && e.clientX <= g.thumbLeft + g.thumb;
        grab = onThumb ? e.clientX - g.thumbLeft : g.thumb / 2;
        held = true;
        pid = e.pointerId;
        track.setPointerCapture(pid);
        track.classList.add('is-held');
        this.rail.classList.add('is-dragging');
        scrollFromX(e.clientX);
        e.preventDefault();
      }, { signal });
      track.addEventListener('pointermove', (e) => {
        if (!held || e.pointerId !== pid) return;
        scrollFromX(e.clientX);
        e.preventDefault();
      }, { signal });
      track.addEventListener('pointerup', end, { signal });
      track.addEventListener('pointercancel', end, { signal });
      track.addEventListener('lostpointercapture', end, { signal });

      track.addEventListener('keydown', (e) => {
        const step = this.rail.clientWidth * 0.8;
        const map = { ArrowRight: step, ArrowLeft: -step, PageDown: step, PageUp: -step };
        if (e.key in map) { e.preventDefault(); this.rail.scrollBy({ left: map[e.key], behavior: 'smooth' }); }
        else if (e.key === 'Home') { e.preventDefault(); this.rail.scrollTo({ left: 0, behavior: 'smooth' }); }
        else if (e.key === 'End') { e.preventDefault(); this.rail.scrollTo({ left: this.rail.scrollWidth, behavior: 'smooth' }); }
      }, { signal });
    }
    disconnectedCallback() {
      if (this.controller) { this.controller.abort(); this.controller = null; }
      if (this.ro) { this.ro.disconnect(); this.ro = null; }
    }
    sync() {
      /* Publish whether this rail can actually scroll. The drag hint and the
         position bar are hidden off data-rail-overflow="0" (the rule lives in
         sections/featured-products.liquid, with the markup it describes): on a
         wide viewport or a short catalogue the rail fits, nothing can be
         dragged, and an affordance that says otherwise is a lie.

         Only ever publish a measurement worth trusting. A rail that is not on
         layout reports zero for both widths, and calling that "no overflow"
         would hide an affordance that is genuinely there — so the attribute is
         removed instead, and the CSS, keyed to the explicit "0", keeps showing
         the affordance. Measurement failure must never be the thing that
         silences a real control. */
      const panel = this.closest('.panel') || this;
      const inner = this.rail.clientWidth;
      const content = this.rail.scrollWidth;
      if (inner > 0 && content > 0) {
        panel.setAttribute('data-rail-overflow', content - inner > 1 ? '1' : '0');
      } else {
        panel.removeAttribute('data-rail-overflow');
      }

      const max = content - inner;
      if (this.prevBtn) {
        const can = inner > 0 && max > 1;
        this.prevBtn.hidden = this.nextBtn.hidden = !can;
        this.prevBtn.disabled = this.rail.scrollLeft <= 2;
        this.nextBtn.disabled = this.rail.scrollLeft >= max - 2;
        const media = this.rail.firstElementChild &&
          this.rail.firstElementChild.querySelector('.card__media, .post__media, .media');
        if (media) {
          const top = media.getBoundingClientRect().top - this.getBoundingClientRect().top + media.offsetHeight / 2;
          if (top > 0) this.style.setProperty('--rail-arrow-y', Math.round(top) + 'px');
        }
      }
      if (!this.bar) return;
      const visible = Math.max(14, Math.min(100, (inner / content) * 100));
      this.bar.style.width = visible + '%';
      this.bar.style.left = (max > 0 ? (this.rail.scrollLeft / max) * (100 - visible) : 0) + '%';
      if (this.track) this.track.setAttribute('aria-valuenow', String(max > 0 ? Math.round((this.rail.scrollLeft / max) * 100) : 0));
    }
  }
  if (!customElements.get('hv-rail')) customElements.define('hv-rail', HvRail);

  /* quantity stepper — used on the PDP and both cart surfaces */
  class HvQuantity extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      if (!this.input) return;
      $$('button', this).forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const step = Number(btn.dataset.step || 1);
          const min = Number(this.input.min || 1);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const next = Math.min(max, Math.max(min, Number(this.input.value || min) + step));
          if (next === Number(this.input.value)) return;
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      this.input.addEventListener('change', () => this.sync());
      this.sync();
    }
    sync() {
      const min = Number(this.input.min || 1);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const value = Number(this.input.value || min);
      $$('button', this).forEach((btn) => {
        const step = Number(btn.dataset.step || 1);
        btn.disabled = step < 0 ? value <= min : value >= max;
      });
    }
  }
  if (!customElements.get('hv-quantity')) customElements.define('hv-quantity', HvQuantity);

  /* table of contents that follows the reader */
  /* ---- CONTENTS RAIL (legal pages, long pages) ---------------------------
     Rebuilt after the Policies & Legal Notices page exposed four problems:
       1. Only h2s that happened to carry an id were listed (88 of 124), so a
          third of the document was missing from the contents.
       2. On desktop the rail is sticky but was 3,800px tall — everything past
          the first ~20 links was unreachable until the end of the page, and
          the highlighted "current" link scrolled out of sight.
       3. On phones it rendered every heading as a pill ABOVE the document:
          3,250px of buttons before the first word of the policy.
       4. The current-section highlight only moved when a heading crossed a
          thin band, so it stuck on the first entry for long stretches.
     Now: every h2 is listed (ids are generated where missing), the list
     scrolls inside its own box and keeps the current entry in view, and on
     phones it is a collapsed, sticky "On this page" button that opens a
     scrollable list. ALL-CAPS headings are shown in title case in the rail
     only — the document text itself is untouched. */
  class HvToc extends HTMLElement {
    connectedCallback() {
      const source = $(this.dataset.source || '.prose');
      if (!source) return;
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;

      const heads = $$('h2', source).filter((h) => h.textContent.trim());
      if (!heads.length) { this.hidden = true; return; }

      const used = new Set($$('[id]', document).map((n) => n.id));
      const slug = (t) => t.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'section';
      heads.forEach((h) => {
        if (h.id) return;
        let id = slug(h.textContent), n = 2;
        while (used.has(id)) id = slug(h.textContent) + '-' + n++;
        used.add(id);
        h.id = id;
      });
      const label = (t) => {
        t = t.replace(/\s+/g, ' ').trim();
        if (/[A-Z]/.test(t) && t === t.toUpperCase()) {
          t = t.toLowerCase().replace(/(^|[\s\u2014(/-])([a-z])/g, (m, a, b) => a + b.toUpperCase());
        }
        return t;
      };
      const esc = (t) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const title = this.dataset.title || 'On this page';

      this.innerHTML =
        `<details class="toc__box">` +
          `<summary class="toc__summary"><span>${esc(title)}</span><em>${heads.length}</em></summary>` +
          `<nav class="toc__list" aria-label="${esc(title)}">` +
            heads.map((h) => `<a href="#${h.id}">${esc(label(h.textContent))}</a>`).join('') +
          `</nav>` +
        `</details>`;
      this.box = $('details', this);
      this.list = $('.toc__list', this);
      this.links = $$('a', this.list);

      /* Open beside the text on desktop; a collapsed button on phones. */
      const mq = matchMedia('(min-width: 1001px)');
      const fit = () => { this.box.open = mq.matches; };
      fit();
      mq.addEventListener('change', fit, { signal });

      /* On a phone, choosing an entry closes the list so the text is visible. */
      this.list.addEventListener('click', (e) => {
        if (e.target.closest('a') && !mq.matches) this.box.open = false;
      }, { signal });

      /* Current section: the last heading whose top has passed 30% of the
         viewport. Measured on scroll (one read per frame), which cannot miss
         a heading the way a thin IntersectionObserver band can. */
      let ticking = false, current = -1;
      const update = () => {
        ticking = false;
        const line = innerHeight * 0.3;
        let idx = 0;
        for (let i = 0; i < heads.length; i++) {
          if (heads[i].getBoundingClientRect().top <= line) idx = i; else break;
        }
        if (idx === current) return;
        current = idx;
        this.links.forEach((a, i) => {
          a.classList.toggle('is-current', i === idx);
          if (i === idx) a.setAttribute('aria-current', 'location'); else a.removeAttribute('aria-current');
        });
        /* keep it in view inside the rail, without scrolling the page */
        const a = this.links[idx], l = this.list;
        if (a && l.scrollHeight > l.clientHeight) {
          const top = a.offsetTop - l.offsetTop;
          if (top < l.scrollTop || top + a.offsetHeight > l.scrollTop + l.clientHeight) {
            l.scrollTop = Math.max(0, top - l.clientHeight / 3);
          }
        }
      };
      addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true, signal });
      addEventListener('resize', update, { signal });
      update();
    }
    disconnectedCallback() { if (this.controller) { this.controller.abort(); this.controller = null; } }
  }
  if (!customElements.get('hv-toc')) customElements.define('hv-toc', HvToc);

  /* localization: submit on change, so it works without a custom dropdown */
  class HvLocalization extends HTMLElement {
    connectedCallback() {
      const select = $('select', this);
      const form = this.querySelector('form');
      if (!select || !form) return;
      select.addEventListener('change', () => {
        const input = $('input[name="country_code"], input[name="locale_code"]', form);
        if (input) input.value = select.value;
        form.submit();
      });
    }
  }
  if (!customElements.get('hv-localization')) customElements.define('hv-localization', HvLocalization);

  /* ── global wiring ─────────────────────────────────────────────────────── */
  document.addEventListener('click', (e) => {
    if (e.target.id === 'hv-scrim' || e.target.closest('[data-overlay-close]')) {
      e.preventDefault();
      HV.overlays.close();
    }
  });

  /* OVERLAY TOGGLES OUTSIDE THE HEADER. data-overlay-toggle was only wired
     inside <hv-toggle> (the header), so the collection "Filter" button and
     the "Search" button in the mobile menu did nothing when tapped. One
     delegated listener now covers every toggle that is not in the header;
     the header keeps its own handler, so nothing fires twice. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-overlay-toggle]');
    if (!btn || btn.closest('hv-toggle')) return;
    const target = $(btn.getAttribute('data-overlay-toggle'));
    if (!target) return;
    e.preventDefault();
    const isOpen = target.classList.contains('is-open');
    HV.overlays.close();
    if (!isOpen) {
      HV.overlays.open(target);
      btn.setAttribute('aria-expanded', 'true');
    }
  });

  addEventListener('keydown', (e) => {
    if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      const search = $('#hv-search');
      if (search) HV.overlays.open(search);
    }
  });

  /* quick add from a product card — one delegated listener for the document */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-quick-add]');
    if (!form) return;
    e.preventDefault();

    /* ⚠️ THE SECOND PRESS WAS ADDING A SECOND ITEM.

       The button only ever gained an `is-loading` CLASS — never `disabled`,
       never `pointer-events:none` — so a click 60ms after the first produced a
       second submit and a second `/cart/add.js`. Measured: 2 requests, "2x" in
       the bag, on every surface (collection grid, homepage rail, product
       recommendations, search). This is a DIFFERENT fault from §4.19's facets
       bug and survived it: that one was collection-only, this one is
       everywhere, which is why "sometimes it adds two" outlived the first fix.

       And the shopper had every reason to press twice: `.is-loading` is styled
       for `.btn`, and `.card__add` is not one, so absolutely nothing happened
       on screen while the fetch was in flight.

       `disabled` is what actually blocks it — a disabled button fires no
       event at all — and the dataset flag covers the case where the button
       cannot be found. */
    if (form.dataset.busy === '1') return;
    form.dataset.busy = '1';
    const button = $('button[type="submit"]', form);
    /* Remember the prior state: a sold-out card renders its button already
       disabled, and this must never re-enable one. */
    const wasDisabled = button ? button.disabled : false;
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }
    try {
      await HV.cart.add(new FormData(form));
      HV.toast(HV.strings.added || 'Added to bag');
      if (form.dataset.quickAdd !== 'silent') {
        const drawer = $('#hv-cart-drawer');
        if (drawer) HV.overlays.open(drawer);
      }
    } catch (err) {
      HV.toast(err.message, true);
    } finally {
      delete form.dataset.busy;
      if (button) {
        button.disabled = wasDisabled;
        button.classList.remove('is-loading');
      }
    }
  });

  /* the theme editor swaps section HTML in place; re-measure the chrome */
  document.addEventListener('shopify:section:load', () => {
    $('hv-header')?.measure?.();
  });

  /* ---- EQUAL DROPDOWN HEIGHTS --------------------------------------------
     Each dropdown used to size to its own content, so New In (short category
     list) opened shorter than Shop All and About. Every panel now takes the
     height of the tallest one, re-measured on resize and once fonts load. */
  const equalizeMegas = () => {
    const panels = $$('.site-nav .mega');
    if (!panels.length) return;
    panels.forEach((p) => { p.style.minHeight = ''; });
    if (matchMedia('(max-width:900px)').matches) return;
    const max = Math.max(...panels.map((p) => p.offsetHeight));
    if (max > 0) panels.forEach((p) => { p.style.minHeight = max + 'px'; });
  };
  let megaTimer;
  const queueEqualize = () => { clearTimeout(megaTimer); megaTimer = setTimeout(equalizeMegas, 120); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queueEqualize);
  else queueEqualize();
  addEventListener('load', queueEqualize);
  addEventListener('resize', queueEqualize);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueEqualize);
})();
